import React, { useState } from 'react';

import classNames from 'classnames';
import { Field } from 'react-final-form';

import css from './IntegerRangeFilter.module.css';

const RADIX = 10;
// Fallback input length when no maximum is configured (4-digit years).
const DEFAULT_MAX_DIGITS = 4;

// The digit cap follows the configured maximum: Birth Year (max 2040) caps at
// 4 digits, price (max e.g. 100000) at 6.
const maxDigitsFor = maxLimit =>
  Number.isInteger(maxLimit) && maxLimit > 0 ? `${maxLimit}`.length : DEFAULT_MAX_DIGITS;

// Strip non-digits and clamp to the max input length.
const sanitizeDigits = (raw, maxDigits) => `${raw}`.replace(/[^0-9]/g, '').slice(0, maxDigits);

const toIntOrNull = str => {
  const parsed = Number.parseInt(str, RADIX);
  return Number.isInteger(parsed) ? parsed : null;
};

// A side is committable when it's empty (open-ended range) or a full in-bounds value.
// Partial or out-of-bounds input is simply not committed (no error state).
const isCommittableSide = (str, min, max) => {
  if (str === '') {
    return true;
  }
  const value = toIntOrNull(str);
  return value != null && value >= min && value <= max;
};

/**
 * Typed From/To range inputs. Either side is optional: a From-only or To-only
 * value commits an open-ended range. Obviously invalid input (partial years,
 * out-of-bounds values) is ignored rather than surfaced as an error — the
 * inputs snap back to the committed value on blur.
 */
const RangeInput = props => {
  const {
    input,
    min: minLimit,
    max: maxLimit,
    isInSideBar,
    allowOpenEnded = false,
    intl,
    getLabelForRangeInput,
  } = props;
  const { value = {}, onChange, name } = input;

  // Drafts hold what the user is typing; null = mirror the committed form value.
  const [drafts, setDrafts] = useState({ minValue: null, maxValue: null });

  const committedString = side => (value?.[side] != null ? `${value[side]}` : '');
  const displayValue = side => (drafts[side] != null ? drafts[side] : committedString(side));

  const commitFromDrafts = (nextDrafts, editedSide) => {
    const minStr = nextDrafts.minValue != null ? nextDrafts.minValue : committedString('minValue');
    const maxStr = nextDrafts.maxValue != null ? nextDrafts.maxValue : committedString('maxValue');
    // The side being edited must be committable; don't fire searches for partial input.
    const editedStr = editedSide === 'minValue' ? minStr : maxStr;
    if (!isCommittableSide(editedStr, minLimit, maxLimit)) {
      return;
    }
    let minValue = isCommittableSide(minStr, minLimit, maxLimit) ? toIntOrNull(minStr) : null;
    let maxValue = isCommittableSide(maxStr, minLimit, maxLimit) ? toIntOrNull(maxStr) : null;
    if (minValue != null && maxValue != null && minValue > maxValue) {
      if (!allowOpenEnded) {
        // Both-sides mode (price): wait until the pair is consistent.
        return;
      }
      // Conflicting pair: the side being edited wins, the other side is dropped.
      if (editedSide === 'minValue') {
        maxValue = null;
      } else {
        minValue = null;
      }
      setDrafts({ minValue: null, maxValue: null, [editedSide]: nextDrafts[editedSide] });
    }
    // Both-sides mode commits complete pairs only (clearing happens via the Clear button).
    if (!allowOpenEnded && (minValue == null || maxValue == null)) {
      return;
    }
    onChange({ minValue, maxValue });
  };

  const maxDigits = maxDigitsFor(maxLimit);

  const handleChange = side => event => {
    const sanitized = sanitizeDigits(event.target.value, maxDigits);
    const nextDrafts = { ...drafts, [side]: sanitized };
    setDrafts(nextDrafts);
    commitFromDrafts(nextDrafts, side);
  };

  // Blur drops any uncommitted draft so the input snaps back to the committed value.
  const handleBlur = side => () => setDrafts(prev => ({ ...prev, [side]: null }));

  const labelForRangeInput = getLabelForRangeInput
    ? getLabelForRangeInput
    : (labelValue, handleName) =>
        intl.formatMessage(
          { id: 'IntegerRangeFilter.screenreader.rangeHandle' },
          { value: labelValue, handle: handleName }
        );

  // Label callbacks (e.g. PriceFilter's currency formatter) need a numeric value;
  // an empty input is announced with its bound instead.
  const ariaLabelFor = side => {
    const numeric = toIntOrNull(displayValue(side));
    const fallback = side === 'minValue' ? minLimit : maxLimit;
    return labelForRangeInput(numeric != null ? numeric : fallback, side === 'minValue' ? 'min' : 'max');
  };

  const classes = isInSideBar ? css.formWrapper : null;

  return (
    <div className={classes}>
      <div className={classNames(css.contentWrapper, { [css.contentWrapperSidebar]: isInSideBar })}>
        <div className={css.inputsWrapper}>
          {!isInSideBar ? (
            <span className={css.labelPopup}>
              {intl.formatMessage({ id: 'IntegerRangeFilter.rangeInputsLabel' })}
            </span>
          ) : null}
          <input
            className={classNames(css.minValue, { [css.valueInSidebar]: isInSideBar })}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={maxDigits}
            autoComplete="off"
            name={`${name}_min`}
            placeholder={intl.formatMessage({ id: 'IntegerRangeFilter.fromPlaceholder' })}
            value={displayValue('minValue')}
            onChange={handleChange('minValue')}
            onBlur={handleBlur('minValue')}
            aria-label={ariaLabelFor('minValue')}
          />
          <span className={css.valueSeparator} aria-hidden="true">
            –
          </span>
          <input
            className={classNames(css.maxValue, { [css.valueInSidebar]: isInSideBar })}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={maxDigits}
            autoComplete="off"
            name={`${name}_max`}
            placeholder={intl.formatMessage({ id: 'IntegerRangeFilter.toPlaceholder' })}
            value={displayValue('maxValue')}
            onChange={handleChange('maxValue')}
            onBlur={handleBlur('maxValue')}
            aria-label={ariaLabelFor('maxValue')}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * FieldSelectIntegerRange component
 *
 * @component
 * @param {Object} props
 * @param {number} [props.min] - The minimum value
 * @param {number} [props.max] - The maximum value
 * @param {string} [props.name] - The name
 * @param {boolean} [props.isInSideBar] - Whether the filter is in the sidebar
 * @param {boolean} [props.allowOpenEnded] - Whether From-only / To-only ranges commit
 * @returns {JSX.Element}
 */
const FieldSelectIntegerRange = props => {
  const { max, min, name, isInSideBar = false, allowOpenEnded = false, ...rest } = props;
  return (
    <Field
      max={max}
      min={min}
      name={name}
      isInSideBar={isInSideBar}
      allowOpenEnded={allowOpenEnded}
      component={RangeInput}
      {...rest}
    />
  );
};

export default FieldSelectIntegerRange;
